import { useRef, useEffect } from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';

import THEME from '../../constants/theme';
import heroVideo from '../../assets/default-background.mp4';

/**
 * Modern HeroBanner component for the homepage
 * Uses the brand video background with updated content for OctopusSync
 */
const HeroBanner = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Video autoplay failed - expected in many browsers
        });
      }
    }
  }, []);

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: THEME.COLORS.NEUTRAL_WHITE,
        overflow: 'hidden',
        zIndex: 0
      }}
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          objectFit: 'cover',
          zIndex: 0
        }}
      >
        <source src={heroVideo} type='video/mp4' />
        Your browser does not support the video tag.
      </video>

      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(101, 17, 175, 0.2) 100%)',
          zIndex: 1
        }}
      />

      {/* Content Container */}
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'left',
          px: { xs: 3, sm: 4, md: 6 },
          maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '75%' },
          ml: { xs: 0, sm: 0, md: -1 }
        }}
      >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={textVariants}>
            <Typography
              component={motion.h2}
              variant='h2'
              sx={{
                fontSize: {
                  xs: '2rem',
                  sm: '2.8rem',
                  md: '3.5rem',
                  lg: '4rem'
                },
                fontWeight: 500,
                marginBottom: 2,
                textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                lineHeight: { xs: 1.3, md: 1.25 },
                letterSpacing: '-0.01em'
              }}
            >
              Real-Time Oversight and Risk Intelligence for{' '}
              <Box 
                component='span' 
                sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.9) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: 'none',
                  filter: 'drop-shadow(0 4px 15px rgba(169, 64, 255, 0.4))'
                }}
              >
                Algorithmic Trading
              </Box>
            </Typography>
          </motion.div>

          <motion.div variants={textVariants}>
            <motion.div
              whileHover={{ x: 12 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Typography
                component={motion.p}
                variant='h5'
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem', lg: '1.6rem' },
                  fontWeight: 400,
                  marginBottom: 3,
                  textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  lineHeight: 1.55,
                  maxWidth: '900px',
                  opacity: 0.92,
                  letterSpacing: '0.3px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    textShadow: '0 4px 25px rgba(101, 17, 175, 0.5), 0 2px 15px rgba(0,0,0,0.4)'
                  }
                }}
              >
                Sentinel is a real-time monitoring and risk analytics system that provides independent oversight of algorithmic trading operations, detecting anomalies and risk build-up before they escalate.
              </Typography>
            </motion.div>
          </motion.div>

          <motion.div variants={textVariants}>
            <motion.div
              whileHover={{ scale: 1.04, x: 12 }}
              transition={{ type: 'spring', stiffness: 280, damping: 18 }}
            >
              <Typography
                component={motion.p}
                variant='h5'
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem', lg: '1.6rem' },
                  fontWeight: 600,
                  marginBottom: 4,
                  textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  lineHeight: 1.6,
                  maxWidth: '850px',
                  color: '#ffffff',
                  letterSpacing: '0.5px',
                  cursor: 'default',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    textShadow: '0 6px 30px rgba(101, 17, 175, 0.7), 0 2px 15px rgba(0,0,0,0.4)',
                    filter: 'brightness(1.2)'
                  }
                }}
              >
                Monitor. Detect. Protect.
              </Typography>
            </motion.div>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroBanner;
